/* eslint-disable @typescript-eslint/no-floating-promises */
import { action, asyncAction, createActions, SB } from '@play-co/replicant';
import { Duo } from '.';
import { DuoItem } from '../buruburu/care/care.ruleset';
import { Feature } from '../analytics';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { getLocale } from '../getters/user';
import { Inventory } from '../buruburu/inventory';
import { getCurrencyGrantEvent } from '../buruburu/inventory/inventory.ruleset';
import { getPSTDate } from '../utils/time';
import {
  generateReplicantChatbotPayload,
  sendChatbotMessage,
} from '../chatbot/chatbotMessage';

export default (
  Duo: Pick<Duo, 'ruleset' | 'api' | 'messages' | 'asyncGetters'>,
  { Care, Pet, History }: Duo['$DEPENDENCIES'],
) =>
  createActions({} as SB.Schema<Duo.MutableState>, {
    messages: Duo.messages,
    computedProperties: {} as any,
    asyncGetters: Duo.asyncGetters,
  })({
    sendDuoInvitation: asyncAction(
      async (
        state,
        { playerId, item }: { playerId: string; item: DuoItem },
        api,
      ) => {
        if (!state.publicProfile) {
          throw new Error('Cannot invite to duo without a public profile');
        }

        const timestamp = api.date.now();

        const { getCurrentEvolution, getPetAge, getPetName } = Pet.getters(
          state,
        );
        const { species } = getCurrentEvolution();

        const pet = await api.asyncGetters.getPetSpecies(playerId);

        state.duo.invitations.sent.push({
          playerId,
          timestamp,
          status: 'pending',
          careItemId: item,
          pet,
          lastReminder: 0,
        });

        const payload = generateReplicantChatbotPayload({
          feature: Feature.DUO._,
          $subFeature: Feature.DUO.INVITE,
        });

        const message = getChatbotFlexMessage({
          template: 'duoInvite',
          payload,
          data: {
            locale: getLocale(state),
            expiration: Duo.ruleset.inviteExpiration,
            item,
            inviter: {
              name: state.publicProfile.name,
              photo: state.publicProfile.photo,
              pet: species,
              petName: state.pet.name,
            },
          },
        });
        // api.chatbot.sendMessage(playerId, message);
        sendChatbotMessage(api, playerId, message, state.platform);

        api.postMessage.sendDuoInvitation(playerId, {
          name: state.publicProfile.name,
          timestamp,
          item,
          senderPet: {
            species,
            name: getPetName(),
            age: getPetAge(timestamp),
          },
        });
      },
    ),

    sendDuoResponse: asyncAction(
      async (
        state,
        {
          playerId,
          timestamp,
          accepted,
          message,
        }: {
          playerId: string;
          timestamp: number;
          accepted: boolean;
          message?: string;
        },
        api,
      ) => {
        const now = api.date.now();

        const { invitations } = state.duo;

        const invitation = invitations.received.find(
          ({ timestamp: inviteTimestamp, playerId: invitePlayerId }) =>
            invitePlayerId === playerId &&
            inviteTimestamp === timestamp &&
            !Duo.api.isExpiredInvitation(inviteTimestamp, now),
        );

        // Invitation not found or expired.
        if (!invitation) {
          return false;
        }

        invitation.status = accepted ? 'accepted' : 'declined';
        invitation.message = message;

        const { getters, ruleset } = Pet;
        const { nextSpeciesWeights } = ruleset;
        const { getCurrentEvolution, getPetName, getPetAge } = getters(state);
        const { species } = getCurrentEvolution();

        // Compute new species if accepted.
        let newSpecies = undefined;
        if (accepted) {
          newSpecies = Duo.api.getNewSpecies(
            [
              nextSpeciesWeights[species],
              nextSpeciesWeights[invitation.pet.species],
            ],
            api.math.random,
          );
        }

        // Only a friend can send an invitation to someone who hasn't set up a public profile.
        // So it's safe to share private profile information.
        const name = state.publicProfile?.name ?? state.profile.name;

        // Should fetch the invitations before they are potentially cleared.
        const friendInvitations = accepted
          ? await api.asyncGetters.getAllDuoInvitations(playerId)
          : [];

        api.postMessage.sendDuoResponse(playerId, {
          status: accepted ? 'accepted' : 'declined',
          message,
          newSpecies,
          timestamp,
          name: state.publicProfile?.name,
        });
        api.sendAnalyticsEvents([
          {
            eventType: 'DuoInviteResponseReceive',
            userId: playerId,
            eventProperties: {
              friend: Object.keys(state.friends).includes(playerId),
              friendsPetId: playerId,
              item: invitation.careItemId,
              count: invitations.received.filter(
                ({ playerId: invitePlayerId }) => invitePlayerId === playerId,
              ).length,
              bPointsSpent:
                Care.ruleset.careItems[invitation.careItemId]?.price,
              response: accepted ? 'accept' : 'refuse',
            },
          },
        ]);

        // Set last response, used to show the response sequence.
        state.duo.lastResponse = {
          playerId,
          timestamp,
          accepted,
          name: state.publicProfile?.name,
        };

        const payload = generateReplicantChatbotPayload({
          feature: Feature.DUO._,
          $subFeature: Feature.DUO.RESPONSE,
        });

        const chatbotMessage = getChatbotFlexMessage({
          template: 'duoResponse',
          payload,
          data: {
            locale: getLocale(state),
            expiration: Duo.ruleset.inviteExpiration,
            item: invitation.careItemId,
            invitee: {
              name,
              photo: state.publicProfile?.photo ?? state.profile.photo,
              pet: species,
              petName: state.pet.name,
            },
          },
        });
        // api.chatbot.sendMessage(playerId, chatbotMessage);
        sendChatbotMessage(api, playerId, chatbotMessage, state.platform);

        // Cancelled, bail.
        if (!accepted) return true;

        // Add info to history.
        History.api.addPlayerDuoRecord(state, {
          species: species,
          name: getPetName(),
          age: getPetAge(now),
          evolutions: state.pet.evolutions,
          friendID: playerId,
          timestamp: now,
        });

        // Set local debut data.
        state.duo.debut = {
          species: newSpecies,
          rewardClaimed: false,
          careItemId: invitation.careItemId,
          inviterId: playerId,
          inviteeId: state.id,
        };

        // If response is accepted, decline all other invitations.
        const allInvitations = [
          ...invitations.received,
          ...invitations.sent,
          ...friendInvitations,
        ];
        allInvitations.forEach(
          ({ playerId: invitePlayerId, timestamp: invitationTimestamp }) => {
            // Already expired, ignore.
            if (Duo.api.isExpiredInvitation(invitationTimestamp, now)) return;
            // Current invitation, ignore.
            if (
              (invitePlayerId === playerId || invitePlayerId === state.id) &&
              invitationTimestamp === timestamp
            ) {
              return;
            }

            const declineMessage =
              getLocale(state) === 'en'
                ? `Sorry, but I've already debuted with someone else.`
                : 'お誘いすごく嬉しいです。でも、もう他の人とデビューするって決めてしまったんです。ごめんなさい。';

            // Send message to player.
            api.postMessage.sendDuoResponse(invitePlayerId, {
              status: 'declined',
              message: declineMessage,
              timestamp: invitationTimestamp,
              name: state.publicProfile?.name,
            });
          },
        );

        // Clear all invitations, except current.
        invitations.received = [invitation];
        invitations.sent = [];

        return true;
      },
    ),

    claimDuoDebutReward: action((state, _, api) => {
      if (!state.duo.debut) {
        throw new Error('Cannot claim debut reward without a debut');
      }

      const { debut } = state.duo;
      if (debut.rewardClaimed) {
        throw new Error('Cannot claim debut reward, already claimed');
      }

      const { getDuoItemReward } = Duo.api;
      const reward = getDuoItemReward(debut.careItemId as DuoItem) ?? 0;

      const { getters, modifiers } = Inventory;
      const { getCurrencies } = getters(state);
      const { addFreeGems } = modifiers(state);
      const now = api.date.now();
      const oldCurrencies = getCurrencies(now);

      addFreeGems(reward);

      debut.rewardClaimed = true;

      // Send CurrencyGrant event
      api.sendAnalyticsEvents([
        getCurrencyGrantEvent(
          {
            feature: Feature.DEBUT._,
            $subFeature:
              debut.inviterId === state.id
                ? Feature.DEBUT.INVITER
                : Feature.DEBUT.INVITEE,
          },
          {
            old: oldCurrencies,
            new: getters(state).getCurrencies(now),
          },
        ),
      ]);
    }),

    debut: action((state, _, api) => {
      const { debut } = state.duo;
      if (!debut) {
        throw new Error('Cannot debut');
      }

      if (!debut.rewardClaimed) {
        throw new Error('Cannot debut, need to claim reward first');
      }

      // Spawn new pet.
      Pet.modifiers(state).spawnPet(
        'debut',
        Pet.ruleset.evolutions[debut.species],
        api.date.now(),
        api.math.random,
        debut.inviterId === state.id ? debut.inviteeId : debut.inviterId,
      );

      // Track pet spawn.
      api.sendAnalyticsEvents([
        {
          eventType: 'PetSpawn',
          userId: state.id,
          eventProperties: {
            feature: Feature.DEBUT._,
            $subFeature:
              debut.inviterId === state.id
                ? Feature.DEBUT.INVITER
                : Feature.DEBUT.INVITEE,
          },
        },
      ]);

      // Clear debut data.
      delete state.duo.debut;

      // Clear invitations.
      state.duo.invitations.received = [];
      state.duo.invitations.sent = [];
    }),

    cancelAllDuoInvitations: action((state, _, api) => {
      const { invitations } = state.duo;
      const allInvitations = [...invitations.received, ...invitations.sent];

      allInvitations.forEach(({ playerId: invitePlayerId, timestamp }) => {
        // Already expired, ignore.
        if (Duo.api.isExpiredInvitation(timestamp, api.date.now())) return;

        // Send message to player.
        api.postMessage.sendDuoResponse(invitePlayerId, {
          status: 'cancelled',
          timestamp,
          name: state.publicProfile?.name,
        });
      });

      // Clear all invitations.
      invitations.received = [];
      invitations.sent = [];
    }),

    sendDuoReminder: action(
      (
        state,
        { playerId, timestamp }: { playerId: string; timestamp: number },
        api,
      ) => {
        // Check if invitation is still valid.
        if (Duo.api.isExpiredInvitation(timestamp, api.date.now())) return;

        const { getCurrentEvolution } = Pet.getters(state);

        const invitation = state.duo.invitations.sent.find(
          (x) => x.playerId === playerId && x.timestamp === timestamp,
        );

        // Invitation not found.
        if (!invitation) {
          throw new Error('Duo invitation not found');
        }

        const jstDay = getPSTDate(api.date.now());
        jstDay.setHours(0, 0, 0, 0);

        const jstLastReminderDay = getPSTDate(invitation.lastReminder);
        jstLastReminderDay.setHours(0, 0, 0, 0);

        // Already reminded today.
        if (jstDay.getTime() === jstLastReminderDay.getTime()) {
          throw new Error('Cannot send duo reminder twice a day');
        }

        const now = api.date.now();
        invitation.lastReminder = now;

        const payload = generateReplicantChatbotPayload({
          feature: Feature.DUO._,
          $subFeature: Feature.DUO.REMINDER,
        });

        const message = getChatbotFlexMessage({
          template: 'duoReminder',
          payload,
          data: {
            locale: getLocale(state),
            expiration: Math.max(
              Duo.ruleset.inviteExpiration - (now - invitation.timestamp),
              0,
            ),
            item: invitation.careItemId,
            inviter: {
              name: state.publicProfile.name,
              photo: state.publicProfile.photo,
              pet: getCurrentEvolution().species,
              petName: state.pet.name,
            },
          },
        });
        // api.chatbot.sendMessage(playerId, message);
        sendChatbotMessage(api, playerId, message, state.platform);
      },
    ),

    updateDuoLastViewed: action((state, _, api) => {
      state.duo.lastViewed = api.date.now();
    }),

    deleteLastDuoResponse: action((state, _, api) => {
      delete state.duo.debut;
      delete state.duo.lastResponse;
    }),

    clearExpiredDuoInvitations: action((state, _, api) => {
      const now = api.date.now() - Duo.ruleset.clearInvitesAfter;

      state.duo.invitations.sent = Duo.api.getNonExpiredSentInvitations(
        state,
        now,
      );

      state.duo.invitations.received = Duo.api.getNonExpiredReceivedInvitations(
        state,
        now,
      );
    }),
  });
